import {useState} from "react"
import {useForm} from "react-hook-form"
import {useNavigate} from "react-router-dom"
import currentAuthSession from "../../aws/cognito/currentAuthSession"
import login from "../../aws/cognito/login"
import firstLogin from "../../aws/cognito/firstLogin"
import sendConfirmationCode from "../../aws/cognito/sendConfirmationCode"
import changePassword from "../../aws/cognito/changePassword"
import {useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import arrowLeftIcon from "../../assets/icon/arrow-left.svg"
import visibilityOff from "../../assets/icon/visibility-off.svg"
import visibilityOn from "../../assets/icon/visibility-on.svg"
import {useSessionContext} from "../../reactContext/contextCustomHooks/useSessionContext"
import {useTeamContext} from "../../reactContext/contextCustomHooks/useTeamContext"
import getTeam from "../../axios/getTeam"

export default function SigninForm() {
    const {t} = useTranslation()
    const {setSessionAction} = useSessionContext()
    const {setTeamAction} = useTeamContext()
    const {register, handleSubmit, setError, clearErrors, formState: {errors}} = useForm({defaultValues: {oldPassword: "", newPassword: ""}})
    const [isPasswordVisibility, setIsPasswordVisibility] = useState(false)
    const [isPasswordSecondVisibility, setIsPasswordSecondVisibility] = useState(false)
    const [resetPassword, setResetPassword] = useState(false)
    const [sendCode, setSendCode] = useState(false)
    const [firstAuth, setFirstAuth] = useState(false)
    let navigate = useNavigate()

    const handleRequest = async (data) => {
        if (resetPassword) {
            changePassword(data)
            .then(() => {
                setResetPassword(false)
                toast.success(t("New password supported!"))
            })
            .catch((error) => {
                console.error("changePassword: ", error)
                toast.error(t("An error has occurred"))
            })
        } else if (firstAuth) {
            firstLogin(data)
            .then((response) => {
                setFirstAuth(false)
                setSessionAction({
                    type: "LOGIN",
                    payload: {
                        isLogin: true,
                        idToken: response.idToken.toString(),
                        idUser: response.idToken.payload.id_user,
                        idTeam: response.idToken.payload.id_team || null,
                        role: response.idToken.payload.role,
                    }
                })
                if (response.idToken.payload.id_team) {
                    getTeam(response.idToken.toString(), response.idToken.payload.id_team).then((response) => {
                        setTeamAction({
                            type: "SET_TEAM_CONTEXT",
                            payload: {
                                licencePackage: response.licence_package,
                                licenceTypes: response.licence_types,
                                licenceStartDate: response.licence_start_date,
                                licenceEndDate: response.licence_end_date,
                            }
                        })
                    })
                }
                navigate("/")
                toast.success(t("Welcome to Neurowide Studio"))
            })
            .catch((error) => {
                console.error("firstAuth: ", error)
            })
        } else {
            await login(data, t, setError)
            .then(async (response) => {
                if (response === undefined || response.nextStep.signInStep === "DONE") {
                    await currentAuthSession()
                    .then((response) => {
                        if (response.accessToken !== undefined) {
                            setSessionAction({
                                type: "LOGIN",
                                payload: {
                                    isLogin: true,
                                    idToken: response.idToken.toString(),
                                    idUser: response.idToken.payload.id_user,
                                    idTeam: response.idToken.payload.id_team || null,
                                    role: response.idToken.payload.role,
                                }
                            })
                            if (response.idToken.payload.id_team) {
                                getTeam(response.idToken.toString(), response.idToken.payload.id_team).then((response) => {
                                    setTeamAction({
                                        type: "SET_TEAM_CONTEXT",
                                        payload: {
                                            licencePackage: response.licence_package,
                                            licenceTypes: response.licence_types,
                                            licenceStartDate: response.licence_start_date,
                                            licenceEndDate: response.licence_end_date,
                                        }
                                    })
                                })
                            }
                            navigate("/")
                            toast.success(t("Welcome to Neurowide Studio"))
                        } else {
                            toast.error(t("Incorrect email or password"))
                            setError("mail", {
                                type: "manual",
                                message: t("Incorrect email or password"),
                            })
                        }
                    })
                    .catch((error) => {
                        console.error("currentAuthSession: ", error)
                    })
                } else if (response.nextStep && response.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                    setFirstAuth(true)
                }
            })
            .catch((error) => {
                console.error("login: ", error)
            })
        }
    }

    const handleVisibility = (type, visibility, second) => {
        var passwordTag = document.getElementById(type)
        passwordTag.type === "password" ? passwordTag.type = "text" : passwordTag.type = "password"
        second ? setIsPasswordSecondVisibility(visibility) : setIsPasswordVisibility(visibility)
    }

    const forgotPassword = (data) => {
        clearErrors()
        if (sendCode) {
            sendConfirmationCode(data.email)
            .then((response) => {
                setSendCode(false)
                if (response.error) {
                    toast.error(t("An error has occurred"))
                } else {
                    setResetPassword(true)
                }
            })
        } else {
            setSendCode(true)
        }
    }

    return (
        <>
        {sendCode ?
            <>
            <form className="signin-form" onSubmit={handleSubmit(forgotPassword)}>
                <button className="previous-btn" onClick={() => {navigate("/login")}}><img src={arrowLeftIcon} alt=""/></button>
                <h2>{t("Forgot password?")}</h2>
                <div className="field">
                    <label for="">Email</label>
                    <input className={`${errors.email ? "not-valid" : ""}`} type="text" id="email" name="email" placeholder="email.exemple@gmail.com" {...register("email", {required: true, pattern: /^([a-zA-Z0-9_\-\.+]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/})}/>
                </div>
                <button>{t("Get my reset code")}</button>
            </form>
            </>
        :
        <>
        {firstAuth ?
            <>
            <form className="signin-form" onSubmit={handleSubmit(handleRequest)}>
                <h2>{t("New password")}</h2>
                {errors.newPassword && <p className="error-message">{t("Your password must contain between 8 and 15 characters including at least one number, one capital letter and a special character.")}</p>}
                <div className="field">
                    <label for="">Email</label>
                    <input className={`${errors.email ? "not-valid" : ""}`} type="text" id="email" name="email" placeholder="email.exemple@gmail.com" {...register("email", {required: true, pattern: /^([a-zA-Z0-9_\-\.+]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/})}/>
                </div>
                <div className="field">
                    <label for="">{t("Temporary password")}</label>
                    <input className={`${errors.oldPassword ? "not-valid" : ""}`} type="password" id="oldPassword" name="oldPassword" placeholder="*****************" {...register("oldPassword", {required: true})}/>
                    <img className="visibility-icon" src={isPasswordVisibility ? visibilityOn : visibilityOff} alt="" onClick={() => handleVisibility("oldPassword", !isPasswordVisibility)}/>
                </div>
                <div className="field">
                    <label for="">{t("New password")}</label>
                    <input className={`${errors.newPassword ? "not-valid" : ""}`} type="password" id="newPassword" name="newPassword" placeholder="*****************" {...register("newPassword", {required: true, pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,15}$/})}/>
                    <img className="visibility-icon" src={isPasswordSecondVisibility ? visibilityOn : visibilityOff} alt="" onClick={() => handleVisibility("newPassword", !isPasswordSecondVisibility, true)}/>
                </div>
                <button onClick={() => clearErrors("mail")}>{t("Send")}</button>
            </form>
            </>
        :
        resetPassword ?
            <>
            <form className="signin-form" onSubmit={handleSubmit(handleRequest)}>
                {errors.newPassword && <p className="error-message">{t("Your password must contain between 8 and 15 characters including at least one number, one capital letter and a special character.")}</p>}
                <h2>{t("Forgot password")}</h2>
                <div className="field">
                    <label for="">Email</label>
                    <input className={`${errors.email ? "not-valid" : ""}`} type="text" id="email" name="email" placeholder="email.exemple@gmail.com" {...register("email", {required: true, pattern: /^([a-zA-Z0-9_\-\.+]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/})}/>
                </div>
                <div className="field">
                    <label for="">{t("Validation code")}</label>
                    <input className={`${errors.code ? "not-valid" : ""}`} type="text" id="code" name="code" placeholder="000000" {...register("code", {required: true, pattern: /^[0-9]{6}$/})}/>
                </div>
                <div className="field">
                    <label for="">{t("New password")}</label>
                    <input className={`${errors.newPassword ? "not-valid" : ""}`} type="password" id="newPassword" name="password" placeholder="*****************" {...register("newPassword", {required: true, pattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,15}$/})}/>
                    <img className="visibility-icon" src={isPasswordVisibility ? visibilityOn : visibilityOff} alt="" onClick={() => handleVisibility("newPassword", !isPasswordVisibility)}/>
                </div>
                <button onClick={() => clearErrors("mail")}>{t("Send")}</button>
            </form>
            </>
        :
            <>
            <form className="signin-form" onSubmit={handleSubmit(handleRequest)}>
                <h2>{t("Log In to Neurowide.studio")}</h2>
                {errors.mail && <p className="error-message">{errors.mail.message}</p>}
                <div className="field">
                    <label for="">Email</label>
                    <input className={`${errors.email ? "not-valid" : ""}`} type="text" id="email" name="email" placeholder="email.exemple@gmail.com" {...register("email", {required: true, pattern: /^([a-zA-Z0-9_\-\.+]+)@([a-zA-Z0-9_\-\.]+)\.([a-zA-Z]+)$/})}/>
                </div>
                <div className="field">
                    <label for="">{t("Password")}</label>
                    <input className={`${errors.password ? "not-valid" : ""}`} type="password" id="password" name="password" placeholder="*****************" {...register("password", {required: true})}/>
                    <img className="visibility-icon" src={isPasswordVisibility ? visibilityOn : visibilityOff} alt="" onClick={() => handleVisibility("password", !isPasswordVisibility)}/>
                </div>
                <div className="field">
                    <span onClick={forgotPassword}>{t("Forgot password?")}</span>
                </div>
                <button onClick={() => clearErrors("mail")}>{t("Log In")}</button>
            </form>
            </>}
        </>}
    </>
    )
}